import React from 'react';
import { cn } from '../../../lib/cn';

/**
 * Spinner, the indeterminate loader.
 *
 * Source selectors: `system.css` `.dsk-spinner` + `--sm`, `--lg`, `--center`.
 *
 * The app's single most repeated component. `LoadingSpinner` appears in 22
 * files and the library shipped the CSS for this without ever shipping the
 * component, which is why every one of those 22 is hand-rolled.
 *
 * Doctrine enforced:
 * - ACCENT, not ink. "Working" is a state the system is reporting, and this is
 *   the one loader that is honest when you cannot know the shape of what is
 *   coming.
 * - When you DO know the shape, use `Skeleton` instead. A spinner over a
 *   layout you can already describe throws away information you have.
 * - A spinner is NEVER the only thing on screen. Under reduced motion the ring
 *   stops but stays visible, so a frozen ring with no words beside it reads as
 *   a hang. `label` is therefore required in spirit: it defaults to "Loading"
 *   and is announced, and `children` renders it visibly.
 * - Inside an ink-filled button the track comes off the ink rather than off the
 *   page, which the CSS already handles via `.dsk-btn--primary .dsk-spinner`.
 *   Nothing needs passing for that.
 *
 *   <Spinner />                             a bare ring, announced as "Loading"
 *   <Spinner size="lg" center>Fetching listings</Spinner>
 */
export const Spinner = React.forwardRef(function Spinner(
  { size = 'md', center = false, label = 'Loading', children, className, ...rest },
  ref,
) {
  const sizeClass = { sm: 'dsk-spinner--sm', md: null, lg: 'dsk-spinner--lg' }[size] || null;

  const ring = (
    <span
      ref={ref}
      className={cn('dsk-spinner', sizeClass, center && !children && 'dsk-spinner--center', className)}
      {...rest}
    />
  );

  // With visible words the ring is decorative and the words do the announcing.
  // Without them the ring itself has to carry the accessible name.
  if (!children) {
    return (
      <span role="status" aria-label={label}>
        {ring}
      </span>
    );
  }

  return (
    <span className={cn('dsa-spinner-row', center && 'dsa-spinner-row--center')} role="status">
      {ring}
      <span className="dsa-spinner-row__label">{children}</span>
    </span>
  );
});
Spinner.displayName = 'Spinner';

/**
 * The full-surface loading state: a centred spinner with a line of text.
 *
 * This is what replaces the "return <LoadingSpinner /> in place of the whole
 * page" pattern that the app repeats everywhere. It keeps the words, so the
 * user is told what is being fetched rather than watching an unexplained ring.
 */
export function LoadingBlock({ children = 'Loading', size = 'lg', className, ...rest }) {
  return (
    <div className={cn('dsa-loadingblock', className)} {...rest}>
      <Spinner size={size} label={typeof children === 'string' ? children : 'Loading'} />
      <p className="dsa-loadingblock__label">{children}</p>
    </div>
  );
}

export default Spinner;
